import { useMutation } from "@apollo/client";
import { useEffect, useState } from "react";
import { AttendeeRole, OnlineConference } from "types/graphql-schema";

import { pendingChatPromptVar } from "@apps/ask-ai/ask-ai-context";
import createOrUpdateMeetingInExternalCalendarMutation from "@apps/meeting-dialog/graphql/create-or-update-meeting-in-external-calendar-mutation";
import useLabel from "@apps/use-label/use-label";
import useUiPreferenceCache, {
  UiPreferenceCache,
} from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import { currentUserVar, errorNotificationVar } from "@cache/cache";
import { useLink } from "@components/link/link-helpers";
import Loading from "@components/loading/loading";
import { meetingEditType } from "@helpers/constants";
import { classNames } from "@helpers/css";

import GetStartedCard from "./get-started-card";
import getProfileOneononesQuery from "./graphql/get-profile-oneonones-query";
import ImportOneononeDialog from "./import-oneonone-dialog";
import { meetingsShowingEmptyCardsVar } from "./oneonone-empty-cards-state";

// This tab always operates on a formal 1-on-1, so prep with the dedicated
// 1-on-1 skill rather than the general (team/group) meeting-prep skill.
const oneononePrepSkill = "/1-on-1-prep";

const displayName = (firstName: string | null | undefined, email: string) =>
  firstName?.trim() || email;

type Props = {
  // Subject of the 1-on-1. Used for the per-profile dismissal pref, the
  // standalone-mode draft attendees, and import-dialog scoping.
  profileId: number;
} & (
  | {
      // In-meeting mode: cards render inside a meeting view. Clicking an option
      // just dismisses for this profile; the meeting itself already exists.
      meetingId: number;
      profileFirstName?: never;
      profileEmail?: never;
    }
  | {
      // Standalone mode (no meeting scheduled yet): Create empty meeting /
      // Prepare with Topicflow AI create a draft via the mutation and redirect
      // to it. profileFirstName/email feed the draft title + attendee list.
      meetingId?: undefined;
      profileFirstName: string | null | undefined;
      profileEmail: string;
    }
);

const OneononeMeetingEmptyCards = ({
  meetingId,
  profileId,
  profileFirstName,
  profileEmail,
}: Props) => {
  const label = useLabel();
  const link = useLink();
  const currentUser = currentUserVar();
  const { uiPreferenceCache, saveUiPreference } = useUiPreferenceCache();
  const [showImportDialog, setShowImportDialog] = useState(false);

  // Track the meeting id (when present) so MeetingDropdownMenu can hide
  // "Discard Draft Meeting" while these cards are showing.
  useEffect(() => {
    if (meetingId === undefined) return;
    const next = new Set(meetingsShowingEmptyCardsVar());
    next.add(meetingId);
    meetingsShowingEmptyCardsVar(next);
    return () => {
      const cleanup = new Set(meetingsShowingEmptyCardsVar());
      cleanup.delete(meetingId);
      meetingsShowingEmptyCardsVar(cleanup);
    };
  }, [meetingId]);

  const meetingTitle =
    profileEmail !== undefined
      ? `${label("1-on-1")}: ${displayName(
          currentUser.firstName,
          currentUser.email,
        )} - ${displayName(profileFirstName, profileEmail)}`
      : "";

  const [createDraftMeeting, { loading: creatingDraft }] = useMutation(
    createOrUpdateMeetingInExternalCalendarMutation,
    {
      refetchQueries: [getProfileOneononesQuery],
      onError: () => {
        errorNotificationVar({
          title: `Could not start your ${label("1-on-1")}`,
          timeout: 5000,
        });
      },
    },
  );

  // useUiPreferenceCache reads a snapshot at render time, so chained
  // saveUiPreference calls all merge against the SAME stale snapshot — later
  // calls clobber earlier ones. We always batch related updates into one call.
  const dismissedProfileIdsAfterDismiss = () => {
    const dismissed = uiPreferenceCache.oneononeEmptyCardsDismissedProfileIds;
    return dismissed.includes(profileId)
      ? dismissed
      : [...dismissed, profileId];
  };

  // Standalone-mode helper: persist the requested prefs (always includes the
  // dismissal so the cards don't reappear on the resulting draft), fire the
  // mutation, then redirect once the meeting is back. `afterCreate` runs with
  // the new meeting id so Prepare-with-AI can prime pendingChatPromptVar.
  const createDraftAndNavigate = (
    extraPrefs: Partial<UiPreferenceCache>,
    afterCreate?: (newMeetingId: number) => void,
  ) => {
    if (profileEmail === undefined) return;
    saveUiPreference({
      oneononeEmptyCardsDismissedProfileIds: dismissedProfileIdsAfterDismiss(),
      ...extraPrefs,
    });
    createDraftMeeting({
      variables: {
        attendees: [
          { email: currentUser.email, role: AttendeeRole.Required },
          { email: profileEmail, role: AttendeeRole.Required },
        ],
        onlineConference: OnlineConference.Native,
        editType: meetingEditType.singleEvent,
        publish: false,
        title: meetingTitle,
        isFormalOneonone: true,
        addTranscriptionBotToMeetingsByDefault: true,
      },
      onCompleted: ({ createOrUpdateMeetingInExternalCalendar }) => {
        const meeting = createOrUpdateMeetingInExternalCalendar?.meeting;
        if (!meeting?.meetingGroup) return;
        afterCreate?.(meeting.id);
        link.redirect(`/profile/${profileId}/oneonone/${meeting.id}`);
      },
    });
  };

  const handleCreateEmpty = () => {
    if (meetingId !== undefined) {
      saveUiPreference({
        oneononeEmptyCardsDismissedProfileIds:
          dismissedProfileIdsAfterDismiss(),
      });
      return;
    }
    createDraftAndNavigate({});
  };

  const handlePrepareWithAI = () => {
    // Open the meeting sidebar (which hosts AskAI) for both the standalone
    // meeting page and the profile 1-on-1 tab. Formal 1-on-1s render in the
    // profile tab, but the standalone key is kept in sync so the prompt fires
    // wherever the user happens to be.
    const sidebarPrefs: Partial<UiPreferenceCache> = {
      profileSidebarExpanded: true,
      meetingSidebarExpanded: true,
    };
    if (meetingId !== undefined) {
      saveUiPreference({
        ...sidebarPrefs,
        oneononeEmptyCardsDismissedProfileIds:
          dismissedProfileIdsAfterDismiss(),
      });
      pendingChatPromptVar({ meetingId, prompt: `${oneononePrepSkill} ` });
      return;
    }
    createDraftAndNavigate(sidebarPrefs, (newMeetingId) => {
      pendingChatPromptVar({
        meetingId: newMeetingId,
        prompt: `${oneononePrepSkill} `,
      });
    });
  };

  // Import notes dismisses only after a successful import so the cards stay
  // visible if the user opens the dialog and cancels out — they can still
  // pick a different option. ImportOneononeDialog redirects to the most recent
  // imported meeting itself, so we only persist the dismissal here.
  const handleImported = () => {
    saveUiPreference({
      oneononeEmptyCardsDismissedProfileIds: dismissedProfileIdsAfterDismiss(),
    });
  };

  if (creatingDraft) {
    return (
      <Loading>Loading {label("oneonone", { titleCase: true })}s...</Loading>
    );
  }

  return (
    <>
      <div
        className={classNames(
          "flex flex-col items-center gap-12 px-6",
          // Standalone view (no meeting yet) centers vertically in the
          // viewport; in-meeting view just flows after the meeting header.
          meetingId === undefined
            ? "min-h-[70vh] justify-center py-12"
            : "pt-16 pb-12",
        )}
      >
        <div className="tf-header-2xl text-center">
          How would you like to start this {label("1-on-1")}?
        </div>
        <div className="w-full max-w-5xl @container">
          <div className="flex flex-col @4xl:flex-row gap-3 @4xl:gap-6 max-w-[500px] @4xl:max-w-none mx-auto">
            <GetStartedCard
              icon="file04"
              title="Create empty meeting"
              body="Start from a blank slate and capture notes right away."
              onClick={handleCreateEmpty}
            />
            <GetStartedCard
              icon="upload"
              title="Import notes"
              body={`Paste notes from past ${label("1-on-1", { pluralize: true })} to bring them into Topicflow.`}
              onClick={() => setShowImportDialog(true)}
            />
            <GetStartedCard
              icon="ai"
              title="Prepare with Topicflow AI"
              body="Let Topicflow AI suggest topics and talking points for this meeting."
              onClick={handlePrepareWithAI}
            />
          </div>
        </div>
      </div>

      {showImportDialog && (
        <ImportOneononeDialog
          profileId={profileId}
          onClose={() => setShowImportDialog(false)}
          onImported={handleImported}
        />
      )}
    </>
  );
};

export default OneononeMeetingEmptyCards;
